import fromUnixTime from 'date-fns/fromUnixTime';
import differenceInDays from 'date-fns/differenceInDays';
import Cookies from 'js-cookie';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import SessionStorage from 'shared/helpers/sessionStorage';
import { emitter } from 'shared/helpers/mitt';
import {
  ANALYTICS_IDENTITY,
  ANALYTICS_RESET,
  CHATWOOT_RESET,
  CHATWOOT_SET_USER,
} from '../../constants/appEvents';

Cookies.defaults = { sameSite: 'Lax' };

export const getLoadingStatus = state => state.fetchAPIloadingStatus;
export const setLoadingStatus = (state, status) => {
  state.fetchAPIloadingStatus = status;
};

export const setUser = user => {
  emitter.emit(CHATWOOT_SET_USER, { user });
  emitter.emit(ANALYTICS_IDENTITY, { user });
};

export const getHeaderExpiry = response =>
  fromUnixTime(response.headers.expiry);

export const setAuthCredentials = response => {
  const expiryDate = getHeaderExpiry(response);
  Cookies.set('cw_d_session_info', JSON.stringify(response.headers), {
    expires: differenceInDays(expiryDate, new Date()),
  });
  setUser(response.data.data, expiryDate);
};

export const clearBrowserSessionCookies = () => {
  Cookies.remove('cw_d_session_info');
  Cookies.remove('auth_data');
  Cookies.remove('user');
};

export const clearLocalStorageOnLogout = () => {
  LocalStorage.remove(LOCAL_STORAGE_KEYS.DRAFT_MESSAGES);
};

export const clearSessionStorageOnLogout = () => {
  SessionStorage.remove(SESSION_STORAGE_KEYS.IMPERSONATION_USER);
};

export const isLogoutInProgress = () => {
  return false;
};

export const clearClientStateOnLogout = async () => {
  await deleteIndexedDBOnLogout();
};

export const deleteIndexedDBOnLogout = async () => {
  let dbs = [];
  try {
    dbs = await window.indexedDB.databases();
    dbs = dbs.map(db => db.name);
  } catch (e) {
    dbs = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
  }

  dbs.forEach(dbName => {
    const deleteRequest = window.indexedDB.deleteDatabase(dbName);

    deleteRequest.onerror = event => {
      // eslint-disable-next-line no-console
      console.error(`Error deleting database ${dbName}.`, event);
    };

    deleteRequest.onsuccess = () => {
      // eslint-disable-next-line no-console
      console.log(`Database ${dbName} deleted successfully.`);
    };
  });

  localStorage.removeItem('cw-idb-names');
};

export const clearCookiesOnLogout = (options = {}) => {
  const { skipClientCleanup = false } = options;
  const globalConfig = window.globalConfig || {};
  const idToken = Cookies.get('cw_keycloak_id_token');

  if (!skipClientCleanup) {
    emitter.emit(CHATWOOT_RESET);
    emitter.emit(ANALYTICS_RESET);
    clearBrowserSessionCookies();
    Cookies.remove('cw_keycloak_id_token');
    Cookies.remove('cw_keycloak_id_token', { path: '/' });
    const relativeUrlRoot = window.chatwootConfig?.websocketURL || '/';
    if (relativeUrlRoot !== '/') {
      Cookies.remove('cw_keycloak_id_token', { path: relativeUrlRoot });
    }
    Cookies.remove('cw_keycloak_refresh_token');
    if (relativeUrlRoot !== '/') {
      Cookies.remove('cw_keycloak_refresh_token', { path: relativeUrlRoot });
    }
    clearLocalStorageOnLogout();
    clearSessionStorageOnLogout();
  }

  const prefix = import.meta.env.VITE_BASE_URL || '/';

  if (globalConfig.KEYCLOAK_ENABLED) {
    const keycloakUrl = (globalConfig.KEYCLOAK_URL || '').replace(/\/$/, '');
    const realm = globalConfig.KEYCLOAK_REALM;
    const clientId = globalConfig.KEYCLOAK_CLIENT_ID;

    if (keycloakUrl && realm) {
      const origin = window.location.origin;
      const cleanPrefix = prefix.replace(/\/$/, '');
      const redirectUri = `${origin}${cleanPrefix}/app/login?logout=true`;

      let logoutUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/logout`;
      if (idToken) {
        logoutUrl += `?id_token_hint=${encodeURIComponent(idToken)}&post_logout_redirect_uri=${encodeURIComponent(redirectUri)}`;
      } else if (clientId) {
        logoutUrl += `?client_id=${encodeURIComponent(clientId)}&post_logout_redirect_uri=${encodeURIComponent(redirectUri)}`;
      } else {
        logoutUrl += `?post_logout_redirect_uri=${encodeURIComponent(redirectUri)}`;
      }

      window.location = logoutUrl;
      return;
    }
  }

  const logoutRedirectLink = globalConfig.LOGOUT_REDIRECT_LINK || prefix;
  window.location = logoutRedirectLink;
};

export const parseAPIErrorResponse = error => {
  if (error?.response?.data?.message) {
    return error?.response?.data?.message;
  }
  if (error?.response?.data?.error) {
    return error?.response?.data?.error;
  }
  if (error?.response?.data?.errors) {
    return error?.response?.data?.errors[0];
  }
  return error;
};

export const throwErrorMessage = error => {
  const errorMessage = parseAPIErrorResponse(error);
  throw new Error(errorMessage);
};

export const parseLinearAPIErrorResponse = (error, defaultMessage) => {
  const errorData = error.response.data;
  const errorMessage = errorData?.error?.errors?.[0]?.message || defaultMessage;
  return errorMessage;
};
